
import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Send, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface FormActionsProps {
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  formSubmitted: boolean;
  isLastStep: boolean;
}

const FormActions = ({
  currentStep,
  onNext,
  onPrev,
  onSubmit,
  isSubmitting,
  formSubmitted,
  isLastStep,
}: FormActionsProps) => {
  if (formSubmitted) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="flex items-center justify-center w-16 h-16 bg-green-500/10 rounded-full">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground">Request Submitted!</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Your refund request has been created successfully.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mobile Actions */}
      <div className="flex flex-col gap-3 sm:hidden">
        {isLastStep ? (
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="w-full h-12 text-base font-medium touch-manipulation"
            size="lg"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Submitting...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Submit Request
              </div>
            )}
          </Button>
        ) : (
          <Button
            onClick={onNext}
            className="w-full h-12 text-base font-medium touch-manipulation"
            size="lg"
          >
            <div className="flex items-center gap-2">
              Continue
              <ArrowRight className="w-4 h-4" />
            </div>
          </Button>
        )}
        
        {currentStep > 1 && (
          <Button
            onClick={onPrev}
            variant="outline"
            className="w-full h-12 text-base font-medium touch-manipulation"
            size="lg"
          >
            <div className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </div>
          </Button>
        )}
      </div>

      {/* Desktop Actions */}
      <div className="hidden sm:flex justify-between items-center">
        <div>
          {currentStep > 1 && (
            <Button
              onClick={onPrev}
              variant="outline"
              className={cn(
                "flex items-center gap-2 h-10 px-4 touch-manipulation",
                "hover:bg-accent/50 transition-colors"
              )}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          )}
        </div>

        <div>
          {isLastStep ? (
            <Button
              onClick={onSubmit}
              disabled={isSubmitting}
              className={cn(
                "flex items-center gap-2 h-10 px-6 touch-manipulation",
                "bg-primary hover:bg-primary/90 transition-colors"
              )}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={onNext}
              className={cn(
                "flex items-center gap-2 h-10 px-6 touch-manipulation",
                "bg-primary hover:bg-primary/90 transition-colors"
              )}
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormActions;
